import {
  lazy,
  Suspense,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactElement,
} from 'react';
import type { RuleSystemId } from '@/domain/campaign';
import { RULE_SYSTEMS } from '@/domain/campaign';
import { parseCampaignFile } from '@/domain/campaign-file';
import { useT } from '@/i18n';
import { useHistoryStatus } from '@/state/history';
import {
  bindAutosaveSource,
  exportCampaignFile,
  flushAutosave,
  importCampaignFile,
  loadAutosave,
  scheduleAutosave,
  showImportError,
} from '@/persistence/storage';
import {
  createCampaign,
  getCampaign,
  getState,
  navigate,
  openSearch,
  redoCampaignEdit,
  replaceCampaign,
  setLocale,
  setToast,
  undoCampaignEdit,
  updateCampaign,
  useAppState,
  type AppRoute,
} from '@/state/store';
import { installKeyboard, registerShortcut } from '@/ui/keyboard';
import { registerHistoryShortcuts } from '@/ui/history-shortcuts';
import ErrorBoundary from '@/ui/ErrorBoundary';
import {
  installRouter,
  isCastRoute,
  isGridRoute,
  isItemsRoute,
  isPlayerRoute,
  isTableRoute,
  playerHomeUrl,
  type RoutedView,
} from '@/ui/router';
import { installCastPublisher } from '@/ui/cast/cast-channel';
import PlayerShell from '@/ui/PlayerShell';
import { printSlug, requestGmScreenPrint } from '@/ui/print/print-target';
import UpdateNotice from '@/ui/UpdateNotice';
import DiceRoller from '@/ui/DiceRoller';
import SearchPalette from '@/ui/SearchPalette';
import SamplePacks, { fetchSampleJson } from '@/ui/views/SamplePacks';
import GmLockGate from '@/ui/views/GmLockGate';
import { useGmUnlocked } from '@/persistence/gm-lock';
// Stats-card numbers come from the build-time generated file
// (npm run stats:content → tools/build-content-stats.mjs), NOT from
// contentStats() in @/content: that module statically imports ~1.3 MB of
// rules/techniques/feats JSON, and importing it here would pin all of it in
// the eager entry chunk while this card only needs six numbers. The views
// that render the rule text are lazy (see below), so the big JSON rides
// along in their chunks. contentStats() still serves every other caller.
import stats from '../content/content-stats.json';

// Per-route views are code-split: each becomes its own chunk loaded on demand.
// The shell (chrome, nav, dice roller, sample packs) stays eager.
const BattleGridView = lazy(() => import('@/ui/views/BattleGridView'));
const BattleTable = lazy(() => import('@/ui/battle/BattleTable'));
const BroadcastShell = lazy(() => import('@/ui/cast/BroadcastShell'));
const CharactersView = lazy(() => import('@/ui/views/CharactersView'));
const ContentView = lazy(() => import('@/ui/views/ContentView'));
const EncountersView = lazy(() => import('@/ui/views/EncountersView'));
const GmScreenView = lazy(() => import('@/ui/views/GmScreenView'));
const ItemsView = lazy(() => import('@/ui/views/ItemsView'));
const MapForgeView = lazy(() => import('@/ui/views/MapForgeView'));
const NpcForgeView = lazy(() => import('@/ui/views/NpcForgeView'));
const NpcsView = lazy(() => import('@/ui/views/NpcsView'));
const PartyView = lazy(() => import('@/ui/views/PartyView'));
const NotesView = lazy(() => import('@/ui/views/NotesView'));
const ReferenceView = lazy(() => import('@/ui/views/ReferenceView'));
const RulesView = lazy(() => import('@/ui/views/RulesView'));
const SessionsView = lazy(() => import('@/ui/views/SessionsView'));
/**
 * The story / prep section is GM-only: it ships in every build, behind the GM
 * password (src/ui/views/GmLockGate.tsx, src/persistence/gm-lock.ts), so the
 * GM can prep on the public site without a player reading over the screen.
 * `VITE_GM_BUILD=0` still builds a site without it at all.
 */
const SHOW_STORY = import.meta.env.VITE_GM_BUILD !== '0';

/** Only loaded when this build ships the story section (see SHOW_STORY). */
const StoryView = SHOW_STORY ? lazy(() => import('@/ui/views/StoryView')) : null;
const WorldView = lazy(() => import('@/ui/views/WorldView'));
// GmScreenPrint statically imports @/content (rules/techniques/feats JSON), so
// it has to load on demand too — otherwise all of that data sits in the entry
// chunk even though only the stats card needs six numbers from it. The boundary
// mounts as soon as a campaign exists (long before anyone clicks Print), so
// requestPrint()'s flushSync still finds the print root subscribed.
const GmScreenPrint = lazy(() =>
  import('@/ui/print/PrintSheets').then((m) => ({ default: m.GmScreenPrint })),
);

interface NavItem {
  /** `RoutedView` = the store's `AppRoute` + `grid` and `items` (registered in ui/router.ts). */
  id: RoutedView;
  phase: number; // §40 phase in which the section lands
}

/**
 * All §40 phases (Phase 1–7) are live and functional!
 */
const CURRENT_PHASE = 7;

const NAV_ITEMS: NavItem[] = [
  { id: 'cockpit', phase: 1 },
  { id: 'sessions', phase: 2 },
  { id: 'npcs', phase: 2 },
  { id: 'forge', phase: 2 },
  { id: 'notes', phase: 2 },
  { id: 'content', phase: 3 },
  { id: 'rules', phase: 3 },
  { id: 'reference', phase: 3 },
  { id: 'gmscreen', phase: 6 },
  { id: 'characters', phase: 3 },
  { id: 'party', phase: 5 },
  { id: 'items', phase: 7 },
  { id: 'encounters', phase: 4 },
  { id: 'world', phase: 5 },
  { id: 'mapforge', phase: 5 },
  { id: 'table', phase: 7 },
  { id: 'grid', phase: 7 },
  { id: 'story', phase: 5 },
];

/** Nav items this build actually ships (the story section is GM-only). */
const NAV_ITEMS_SHIPPED: NavItem[] = SHOW_STORY
  ? NAV_ITEMS
  : NAV_ITEMS.filter((item) => item.id !== 'story');

export default function App(): ReactElement {
  const { campaign, ui } = useAppState();
  // `RoutedView` = AppRoute + `grid`/`items` (the battle grid and the item
  // manager register their ids in ui/router.ts, not in the store's RouteId).
  // Same value as ui.route; just the vocabulary this shell's switch below
  // speaks.
  const route: RoutedView = ui.route;
  // The bundled sample is a GM pack: its button shows once the GM lock is open.
  const gmUnlocked = useGmUnlocked();
  const history = useHistoryStatus();
  const t = useT();
  const [nameInput, setNameInput] = useState('');
  const importRef = useRef<HTMLInputElement>(null);

  // ---- hash routing: adopt the URL in a LAYOUT effect, before the first
  // paint (a shared `#/play` link must never flash the GM chrome) and before
  // every passive effect — so the route prefix has picked the storage
  // namespace (atla-gm:* vs atla-player:*) by the time loadAutosave() runs
  // below. It starts the two-way hash sync; the returned uninstaller keeps
  // StrictMode's double mount to exactly one router.
  useLayoutEffect(() => installRouter(), []);

  // ---- persistence wiring (once)
  useEffect(() => {
    bindAutosaveSource(getCampaign);
    const restored = loadAutosave();
    if (restored && !getCampaign()) {
      replaceCampaign(restored.campaign);
    }
  }, []);

  // ---- debounced autosave whenever the campaign object changes
  useEffect(() => {
    if (campaign) scheduleAutosave();
  }, [campaign]);

  // ---- the broadcast screen follows this window live (GM surface only: the
  // `#/cast` window itself is the listener, never a publisher)
  useEffect(() => (isCastRoute(getState().ui.route) ? undefined : installCastPublisher()), []);

  // ---- flush on page close
  useEffect(() => {
    const flush = (): void => flushAutosave();
    window.addEventListener('beforeunload', flush);
    return () => window.removeEventListener('beforeunload', flush);
  }, []);

  // ---- global shortcuts
  useEffect(() => {
    const offs = [
      registerShortcut({
        combo: 'ctrl+s',
        descriptionKey: 'shortcuts.ctrlS',
        handler: () => flushAutosave(),
      }),
      registerShortcut({
        combo: 'ctrl+k',
        descriptionKey: 'shortcuts.ctrlK',
        // Ctrl/Cmd-K is ONE shortcut with two destinations: when the GM screen
        // is mounted its own search box is the search the GM means, so focus
        // that (by its public id — the shell never reaches into React state);
        // everywhere else the campaign palette opens as before.
        handler: () => {
          const gmSearch = document.getElementById('gm-screen-search');
          if (gmSearch instanceof HTMLInputElement) {
            gmSearch.focus();
            gmSearch.select();
          } else {
            openSearch();
          }
        },
      }),
      // Undo/redo lives on the SAME registry (not a second listener) with
      // the registry's typing guard, so Ctrl+Z in a text field stays the
      // browser's native undo — see src/ui/history-shortcuts.ts.
      registerHistoryShortcuts(),
    ];
    const uninstall = installKeyboard();
    return () => {
      offs.forEach((off) => off());
      uninstall();
    };
  }, []);

  // ---- rulebook of the ACTIVE locale: start fetching it once, early
  // (fire-and-forget). @/content is imported dynamically so this entry chunk
  // never gains a static edge to the ~0.9 MB of content JSON; the cache inside
  // loadRuleDocument() makes the boot call and every later call share one
  // fetch. On a locale switch the newly selected book starts loading here
  // while the views re-render (RulesView/print also ask for it themselves).
  useEffect(() => {
    void import('@/content')
      .then((content) => content.loadRuleDocument(ui.locale))
      .catch(() => undefined);
  }, [ui.locale]);

  // ---- toast auto-hide
  useEffect(() => {
    if (!ui.toastKey) return undefined;
    const id = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(id);
  }, [ui.toastKey]);

  // ---- player surface (`#/play…`): same store, same wiring, other shell.
  // Everything above runs for BOTH surfaces (router, autosave, shortcuts,
  // toasts); everything below is GM chrome and never mounts under a player
  // route — the GM-only views are absent (src/ui/PlayerShell.tsx), not
  // hidden.
  if (isPlayerRoute(ui.route)) {
    return <PlayerShell />;
  }

  // ---- broadcast screen (`#/cast`): chrome-less, read-only, its own
  // storage namespace — the second window the GM shares or puts on the TV.
  if (isCastRoute(ui.route)) {
    return (
      <Suspense fallback={null}>
        <BroadcastShell />
      </Suspense>
    );
  }

  /**
   * Copy the player home URL (`location.origin + pathname + '#/play'`) to
   * the clipboard. Graceful when the Clipboard API is missing or refuses
   * (file:// pages, denied permission): a hidden textarea + the legacy
   * execCommand copy is tried next, and if even that fails the toast shows
   * the URL so the GM can copy it by hand.
   */
  const copyPlayerLink = async (): Promise<void> => {
    const url = playerHomeUrl();
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
        setToast('playerlink.copied');
        return;
      }
    } catch {
      // permission refused / insecure context — fall through to the fallback
    }
    try {
      const ta = document.createElement('textarea');
      ta.value = url;
      ta.setAttribute('readonly', '');
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      const copied = document.execCommand('copy');
      document.body.removeChild(ta);
      if (copied) {
        setToast('playerlink.copied');
        return;
      }
    } catch {
      // execCommand unavailable too — show the URL instead
    }
    setToast('playerlink.manual', { url });
  };

  const onCreate = (ruleSystem: RuleSystemId): void => {
    const fallback =
      ui.locale === 'tr' ? 'Adsız Kampanya' : 'Untitled Campaign';
    createCampaign(nameInput.trim() || fallback, ruleSystem);
    setNameInput('');
  };

  /** Load the bundled sample (public/samples) through the normal importer. */
  const loadSample = async (): Promise<void> => {
    let json: unknown;
    try {
      // The same GM-locked path as the pack list (src/ui/views/SamplePacks.tsx).
      json = await fetchSampleJson('my-atla-campaign.atlagm');
    } catch {
      setToast('sample.error');
      return;
    }
    try {
      const parsed = parseCampaignFile(json);
      replaceCampaign(parsed.campaign);
      setToast('sample.loaded');
    } catch (err) {
      showImportError(err, 'sample.error');
    }
  };

  return (
    <div className="shell">
      <header className="topbar">
        <div>
          <h1>{t('app.title')}</h1>
          <div className="subtitle">{t('app.subtitle')}</div>
        </div>
        <div className="spacer" />
        <DiceRoller />
        {/* Undo/Redo pair — honestly disabled: greyed out whenever there is
            nothing to undo or redo (state/history.ts status, bilingual t()). */}
        <div className="history-toggle">
          <button
            type="button"
            onClick={() => undoCampaignEdit()}
            disabled={!history.canUndo}
            aria-label={t('history.undo')}
            title={t('history.undoHint')}
          >
            {t('history.undo')}
          </button>
          <button
            type="button"
            onClick={() => redoCampaignEdit()}
            disabled={!history.canRedo}
            aria-label={t('history.redo')}
            title={t('history.redoHint')}
          >
            {t('history.redo')}
          </button>
        </div>
        <span
          className={`save-status${ui.saveStatus === 'error' ? ' error' : ''}`}
          role="status"
          aria-live="polite"
        >
          {ui.saveStatus !== 'idle' ? t(`save.${ui.saveStatus}`) : ''}
        </span>
        {/* The table's front door: copies `…#/play` (see copyPlayerLink). */}
        <button
          type="button"
          className="btn secondary player-link-btn"
          onClick={() => void copyPlayerLink()}
        >
          {t('playerlink.button')}
        </button>
        <div className="locale-toggle" aria-label="language">
          <button
            type="button"
            className={ui.locale === 'tr' ? 'active' : ''}
            onClick={() => setLocale('tr')}
          >
            TR
          </button>
          <button
            type="button"
            className={ui.locale === 'en' ? 'active' : ''}
            onClick={() => setLocale('en')}
          >
            EN
          </button>
        </div>
      </header>

      <nav className="sidebar" aria-label="sections">
        <div className="nav-section">{t('app.title')}</div>
        {NAV_ITEMS_SHIPPED.map((item) => (
          <button
            key={item.id}
            type="button"
            data-route={item.id}
            className={`nav-btn${route === item.id ? ' active' : ''}`}
            onClick={() => navigate(item.id as AppRoute)}
          >
            <span>{t(`nav.${item.id}`)}</span>
            {item.phase > CURRENT_PHASE ? (
              <span className="phase">
                {t('nav.phaseNote', { phase: item.phase })}
              </span>
            ) : null}
          </button>
        ))}
      </nav>

      <main className="main">
        {/* Inner boundary (same component as main.tsx): a broken view stays
            inside <main> — sidebar, export button and stats keep working.
            Keyed by route, so navigating away remounts it and retries. */}
        <ErrorBoundary key={ui.route}>
          <Suspense
            fallback={
              <section className="card" aria-busy="true">
                <p className="muted">…</p>
              </section>
            }
          >
          {isGridRoute(route) ? (
            <BattleGridView />
          ) : isTableRoute(route) ? (
            <BattleTable />
          ) : route === 'cockpit' ? (
            campaign ? (
              <CampaignPanel
                name={campaign.name}
                ruleSystem={campaign.ruleSystem}
                era={campaign.era}
                synopsis={campaign.synopsis}
                locale={ui.locale}
              />
            ) : (
              <section className="card">
                <h2>{t('cockpit.noCampaignTitle')}</h2>
                <p className="muted">{t('cockpit.noCampaignBody')}</p>
                <label className="field" htmlFor="campaign-name">
                  {t('cockpit.name')}
                </label>
                <input
                  id="campaign-name"
                  type="text"
                  value={nameInput}
                  placeholder={
                    ui.locale === 'tr'
                      ? 'Örn. Ateş Ulusu İttifakı'
                      : 'e.g. Fire Nation Alliance'
                  }
                  onChange={(e) => setNameInput(e.target.value)}
                />
                <div className="btn-row">
                  <button type="button" className="btn" onClick={() => onCreate('fate')}>
                    {t('cockpit.newFate')}
                  </button>
                  <button
                    type="button"
                    className="btn secondary"
                    onClick={() => onCreate('dnd5e-atla')}
                  >
                    {t('cockpit.newDnd')}
                  </button>
                  <button
                    type="button"
                    className="btn secondary"
                    onClick={() => onCreate('mixed')}
                  >
                    {t('cockpit.newMixed')}
                  </button>
                  {gmUnlocked ? (
                    <button
                      type="button"
                      className="btn secondary"
                      onClick={() => void loadSample()}
                    >
                      {t('cockpit.loadSample')}
                    </button>
                  ) : null}
                </div>
              </section>
            )
          ) : route === 'sessions' ? (
            <SessionsView />
          ) : route === 'npcs' ? (
            <NpcsView />
          ) : route === 'forge' ? (
            <NpcForgeView />
          ) : route === 'notes' ? (
            <NotesView />
          ) : route === 'characters' ? (
            <CharactersView />
          ) : route === 'party' ? (
            <PartyView />
          ) : isItemsRoute(route) ? (
            <ItemsView />
          ) : route === 'content' ? (
            <ContentView />
          ) : route === 'rules' ? (
            <RulesView />
          ) : route === 'reference' ? (
            <ReferenceView />
          ) : route === 'gmscreen' ? (
            <GmScreenView />
          ) : route === 'encounters' ? (
            <EncountersView />
          ) : route === 'world' ? (
            <WorldView />
          ) : route === 'mapforge' ? (
            <MapForgeView />
          ) : route === 'story' && StoryView ? (
            <GmLockGate id="story" label={t('nav.story')}>
              <StoryView />
            </GmLockGate>
          ) : (
            <section className="card">
              <h2>{t(`nav.${ui.route}`)}</h2>
              <p className="muted">{t('nav.phaseNote', { phase: navPhase(ui.route) })}</p>
            </section>
          )}
          </Suspense>
        </ErrorBoundary>

        <section className="card">
          <h2>{t('stats.title')}</h2>
          <div className="stats-grid">
            <Stat value={stats.techniques} label={t('stats.techniques')} />
            <Stat value={stats.researchedTechniques} label={t('stats.researched')} />
            <Stat value={stats.fightingTechniques} label={t('stats.fighting')} />
            <Stat value={stats.feats} label={t('stats.feats')} />
            <Stat value={stats.removedFeats} label={t('stats.removedFeats')} />
            <Stat value={stats.ruleEntries} label={t('stats.rules')} />
          </div>
          <p className="source-note">
            ATLA 5e Homebrew · Fate v0.04 Türkçe Playtest —{' '}
            {ui.locale === 'tr' ? 'sayfa izlenebilirliği ile' : 'with page traceability'}
          </p>
        </section>

        <section className="card">
          <h2>{t('transfer.export')}</h2>
          <div className="btn-row">
            <button
              type="button"
              className="btn"
              disabled={!campaign}
              onClick={() => campaign && exportCampaignFile(campaign)}
            >
              {t('transfer.export')}
            </button>
            <button
              type="button"
              className="btn secondary"
              onClick={() => importRef.current?.click()}
            >
              {t('transfer.import')}
            </button>
            <button
              type="button"
              className="btn secondary"
              disabled={!campaign}
              onClick={() =>
                campaign &&
                // Awaited: the GM screen prints a rule index, and the active
                // locale's rulebook is fetched on demand (usually already
                // resolved by the boot load below).
                void requestGmScreenPrint(
                  `${printSlug(campaign.name)}-gm-ekrani`,
                  ui.locale,
                )
              }
            >
              {t('print.gmScreen')}
            </button>
            <input
              ref={importRef}
              type="file"
              accept=".atlagm,.json,application/json"
              hidden
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) void importCampaignFile(file);
                e.target.value = '';
              }}
            />
          </div>
        </section>

        <SamplePacks />

        <section className="card">
          <h2>{t('shortcuts.title')}</h2>
          <ul className="shortcut-list">
            <li>
              <kbd className="kbd">Ctrl</kbd>
              <kbd className="kbd">K</kbd>
              {t('shortcuts.ctrlK')}
            </li>
            <li>
              <kbd className="kbd">Ctrl</kbd>
              <kbd className="kbd">S</kbd>
              {t('shortcuts.ctrlS')}
            </li>
            <li>
              <kbd className="kbd">Ctrl</kbd>
              <kbd className="kbd">Z</kbd>
              {t('shortcuts.undo')}
            </li>
            <li>
              <kbd className="kbd">Ctrl</kbd>
              <kbd className="kbd">Shift</kbd>
              <kbd className="kbd">Z</kbd>
              {t('shortcuts.redo')}
            </li>
            <li>
              <kbd className="kbd">/</kbd>
              {t('gmScreen.search.shortcut')}
            </li>
          </ul>
        </section>
      </main>

      {ui.toastKey ? (
        <div className="toast">{t(ui.toastKey, ui.toastParams ?? undefined)}</div>
      ) : null}
      {/* Offline update pill — only ever appears when a new service-worker
          version is waiting, and only a click on it reloads the page. */}
      <UpdateNotice />
      {campaign ? (
        <Suspense fallback={null}>
          <GmScreenPrint campaign={campaign} locale={ui.locale} />
        </Suspense>
      ) : null}
      <SearchPalette />
    </div>
  );
}

function navPhase(route: RoutedView): number {
  return NAV_ITEMS.find((item) => item.id === route)?.phase ?? 1;
}

function Stat({ value, label }: { value: number; label: string }): ReactElement {
  return (
    <div className="stat">
      <div className="value">{value}</div>
      <div className="label">{label}</div>
    </div>
  );
}

interface CampaignPanelProps {
  name: string;
  ruleSystem: RuleSystemId;
  era: string | null;
  synopsis: string;
  locale: 'tr' | 'en';
}

function CampaignPanel(props: CampaignPanelProps): ReactElement {
  const t = useT();
  const descriptor = RULE_SYSTEMS[props.ruleSystem];
  const systemLabel =
    props.locale === 'tr' ? descriptor.label.tr : descriptor.label.en;

  return (
    <section className="card">
      <h2>{props.name}</h2>
      <div>
        <span className="chip">
          {t('cockpit.ruleSystem')}: {systemLabel}
        </span>
        <span className="chip">{descriptor.sourceDocument}</span>
      </div>
      <label className="field" htmlFor="campaign-era">
        {t('cockpit.era')}
      </label>
      <input
        id="campaign-era"
        type="text"
        value={props.era ?? ''}
        placeholder={t('cockpit.eraPlaceholder')}
        onChange={(e) => updateCampaign((c) => ({ ...c, era: e.target.value || null }))}
      />
      <label className="field" htmlFor="campaign-synopsis">
        {t('cockpit.synopsis')}
      </label>
      <textarea
        id="campaign-synopsis"
        value={props.synopsis}
        onChange={(e) => updateCampaign((c) => ({ ...c, synopsis: e.target.value }))}
      />
    </section>
  );
}
